import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Layout } from "../components/Layout";
import { useI18n } from "../lib/i18n";
import { VerifyBadge } from "../components/Badges";
import { Users, Briefcase, FileMagnifyingGlass, Warning, ShieldCheck, Plus, Trash } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

function StatBox({ label, value, icon: Icon, tid }) {
  return (
    <div className="as-card p-5" data-testid={tid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">{label}</span>
        <Icon size={22} weight="duotone" color="#080F14" />
      </div>
      <div className="text-3xl font-extrabold mt-2 text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{value}</div>
    </div>
  );
}

export default function Admin() {
  const { t } = useI18n();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [filterRole, setFilterRole] = useState("");
  const [badgeInput, setBadgeInput] = useState({});

  const loadAll = async () => {
    const [s, u, f] = await Promise.all([
      api.get("/admin/stats"),
      api.get(filterRole ? `/admin/users?role=${filterRole}` : "/admin/users"),
      api.get("/admin/flagged-media"),
    ]);
    setStats(s.data); setUsers(u.data); setFlagged(f.data);
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filterRole]);

  const setKyc = async (id, status) => {
    try { await api.put(`/admin/users/${id}/kyc`, { status }); toast.success("Updated"); loadAll(); }
    catch { toast.error(t("error_occurred")); }
  };
  const addBadge = async (id) => {
    const b = (badgeInput[id] || "").trim(); if (!b) return;
    try { await api.post(`/admin/users/${id}/badge`, { badge: b }); toast.success("Badge added"); setBadgeInput({...badgeInput, [id]: ""}); loadAll(); }
    catch { toast.error(t("error_occurred")); }
  };
  const removeBadge = async (id, b) => {
    try { await api.delete(`/admin/users/${id}/badge?badge=${encodeURIComponent(b)}`); loadAll(); }
    catch { toast.error(t("error_occurred")); }
  };

  return (
    <Layout>
      <Toaster richColors position="top-center" />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)]">Admin</div>
        <h1 className="text-3xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{t("admin_dashboard")}</h1>

        <div className="flex gap-2 mt-6 border-b border-[var(--as-border)]">
          {["overview", "users", "flagged"].map((tk) => (
            <button key={tk} onClick={() => setTab(tk)}
              className={`px-4 py-3 font-bold text-sm capitalize ${tab === tk ? "text-[var(--as-accent)] border-b-2 border-[var(--as-accent)]" : "text-[var(--as-text-secondary)]"}`}
              data-testid={`admin-tab-${tk}`}>
              {tk}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <StatBox label="Total users" value={stats.total_users} icon={Users} tid="admin-stat-total" />
            <StatBox label="Verified" value={stats.verified_users} icon={ShieldCheck} tid="admin-stat-verified" />
            <StatBox label="Opportunities" value={stats.total_opportunities} icon={Briefcase} tid="admin-stat-ops" />
            <StatBox label="Applications" value={stats.total_applications} icon={FileMagnifyingGlass} tid="admin-stat-apps" />
            <StatBox label="Skilled individuals" value={stats.by_role.skilled_individual} icon={Users} tid="admin-stat-individuals" />
            <StatBox label="MSMEs" value={stats.by_role.msme} icon={Users} tid="admin-stat-msmes" />
            <StatBox label="Employers" value={stats.by_role.employer} icon={Users} tid="admin-stat-employers" />
            <StatBox label="Flagged media" value={stats.flagged_media} icon={Warning} tid="admin-stat-flagged" />
          </div>
        )}

        {tab === "users" && (
          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-3">
              <label className="as-label !mb-0">Role:</label>
              <select className="as-input !w-auto !py-2" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} data-testid="admin-role-filter">
                <option value="">All</option>
                <option value="skilled_individual">Individual</option>
                <option value="msme">MSME</option>
                <option value="employer">Employer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="as-card p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--as-bg-alt)] text-[var(--as-text-secondary)]">
                  <tr>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Role</th>
                    <th className="text-left p-3">City</th>
                    <th className="text-left p-3">KYC</th>
                    <th className="text-left p-3">Badges</th>
                    <th className="text-left p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-[var(--as-border)]" data-testid={`admin-row-${u.id}`}>
                      <td className="p-3">
                        <Link to={`/profile/${u.id}`} className="font-bold hover:underline">{u.name}</Link>
                        <div className="text-xs text-[var(--as-text-secondary)]">{u.email}</div>
                      </td>
                      <td className="p-3">{u.role.replace("_", " ")}</td>
                      <td className="p-3">{u.city || "—"}</td>
                      <td className="p-3"><VerifyBadge status={u.kyc_status} /></td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(u.badges || []).map((b) => (
                            <span key={b} className="as-badge-skill !text-xs flex items-center gap-1">
                              {b}<button onClick={() => removeBadge(u.id, b)} className="opacity-60 hover:opacity-100">×</button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-1 mt-1.5">
                          <input className="as-input !py-1 !text-xs !w-32" placeholder="Badge name" value={badgeInput[u.id] || ""} onChange={(e) => setBadgeInput({...badgeInput, [u.id]: e.target.value})} data-testid={`admin-badge-input-${u.id}`} />
                          <button onClick={() => addBadge(u.id)} className="btn-secondary !py-1 !px-3 !text-xs" data-testid={`admin-add-badge-${u.id}`}><Plus size={12} weight="bold" /></button>
                        </div>
                      </td>
                      <td className="p-3 space-x-1">
                        <button onClick={() => setKyc(u.id, "verified")} disabled={u.kyc_status === "verified"} className="btn-secondary !py-1 !px-3 !text-xs" data-testid={`admin-verify-${u.id}`}>Verify</button>
                        <button onClick={() => setKyc(u.id, "pending")} className="btn-secondary !py-1 !px-3 !text-xs">Pending</button>
                        <button onClick={() => setKyc(u.id, "unverified")} className="btn-secondary !py-1 !px-3 !text-xs">Unverify</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "flagged" && (
          <div className="mt-6">
            {flagged.length === 0 ? (
              <div className="as-card p-10 text-center text-[var(--as-text-secondary)]">No flagged content. Our trust & safety pipeline is on duty.</div>
            ) : (
              <div className="as-card p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--as-bg-alt)] text-[var(--as-text-secondary)]">
                    <tr>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3">Title</th>
                      <th className="text-left p-3">Reason</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flagged.map((m) => (
                      <tr key={m.id} className="border-t border-[var(--as-border)]" data-testid={`flagged-${m.id}`}>
                        <td className="p-3">{m.user_id}</td>
                        <td className="p-3">{m.title}</td>
                        <td className="p-3 text-amber-700">{m.moderation?.reason || "—"}</td>
                        <td className="p-3">{m.type}</td>
                        <td className="p-3 text-xs">{new Date(m.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
